import apiClient from "../../client/apiClient"
import { appendQueryParams } from "../../utils"

export type AttendanceStatus = "present" | "absent" | "late" | "half-day" | "leave" | ""

export type InOutRecord = {
  type: "in" | "out"
  time: string
}

export type StudentAttendanceRecord = {
  enrollmentId: string
  status: AttendanceStatus
  remarks?: string
  date?: string
  checkIn?: string
}

export type MarkStudentAttendanceRequest = {
  attendances: Array<{
    enrollmentId: string;
    status?: AttendanceStatus; 
    remarks?: string;
    date: string;
    in?: string;
    out?: string;
  }>;
  markedBy: string;
}

export type StudentAttendanceResponse = {
  enrollmentId: string
  user: {
    _id: string
    firstName: string
    lastName: string
  }
  student: {
    _id: string
    grNumber: string
  }
  class: {
    _id: string
    name: string
  }
  section: {
    _id: string
    name: string
  }
  branch: string
  session: string
  date: string
  // Backend-resolved status: the actual recorded status if marked, otherwise
  // the campus's configured default (Present/Absent) for that day — never
  // hardcode a fallback here, there's no "Unmarked" state.
  status: AttendanceStatus
  attendance: {
    _id: string
    enrollment: string
    status: AttendanceStatus
    markedBy: string
    remarks?: string
    date: string
    inOut?: InOutRecord[]
    createdAt: string
    updatedAt: string
  } | null
  latestIn?: string
  latestOut?: string
}

export type StudentAttendanceApiResponse = {
  success: boolean
  message: string
  data?: any[]
  invalidIds?: string[]
  error?: string
  // Rows the server refused — a TC'd, struck-off, or left-without-
  // information student has nothing to mark. Always present (possibly
  // empty), never a silent drop.
  skipped?: { enrollmentId: string; reason: string }[]
}

export type StudentAttendanceListResponse = {
  success: boolean
  message: string
  data: StudentAttendanceResponse[]
  pagination: {
    total: number
    totalPages: number
    currentPage: number
    limit: number
    hasNextPage: boolean
    hasPrevPage: boolean
  }
}

export const isValidAttendanceStatus = (
  status: string
): status is AttendanceStatus => {
  return ["present", "absent", "late", "half-day", "leave"].includes(status)
}

const ATTENDANCE_ENDPOINT = {
  MARK_STUDENT_ATTENDANCE: "/attendance/mark-student",
  STUDENT_ATTENDANCE: "/attendance/student-attendance",
  RESET_STUDENT_ATTENDANCE: "/attendance/reset-student-attendance",
}

const markStudentAttendance = async (
  data: MarkStudentAttendanceRequest
): Promise<StudentAttendanceApiResponse> => {
  const response = await apiClient({
    url: ATTENDANCE_ENDPOINT.MARK_STUDENT_ATTENDANCE,
    method: "POST",
    data,
  })
  return response.data
}

const getStudentAttendance = async ({
  classId,
  sectionId,
  groupId,
  branchId,
  sessionId,
  date,
  search = "",
  sortBy = "createdAt",
  sortOrder = "desc",
  page = 1,
  limit = 10,
  systemId,
}: {
  classId?: string
  sectionId?: string
  groupId?: string
  branchId?: string
  sessionId?: string
  date?: string
  search?: string
  sortBy?: string
  sortOrder?: "asc" | "desc"
  page?: number
  limit?: number
  systemId?: string
} = {}): Promise<StudentAttendanceListResponse> => {
  const response = await apiClient({
    url: appendQueryParams(ATTENDANCE_ENDPOINT.STUDENT_ATTENDANCE, {
      classId,
      sectionId,
      groupId,
      branchId,
      sessionId,
      date,
      search,
      sortBy,
      sortOrder,
      page,
      limit,
      systemId,
    }),
    method: "GET",
  })
  return response.data
}

 const resetStudentAttendance = async ({
  date,
  enrollmentIds,
}: {
  date: string;
  enrollmentIds: string[];
}): Promise<{ success: boolean; message: string; deletedCount: number }> => {
  const response = await apiClient({
    url: ATTENDANCE_ENDPOINT.RESET_STUDENT_ATTENDANCE,
    method: "DELETE",
    data: {
      date,
      enrollmentIds,
    },
  });

  return response.data;
};
export { markStudentAttendance, getStudentAttendance, resetStudentAttendance };